import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Pencil, Trash2, MapPin, X, Camera, Tv } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import toast from 'react-hot-toast';
import { branchesApi, camerasApi, mediaPlayersApi } from '../services/api';
import type { Branch } from '../types';

export default function Branches() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Branch | null>(null);
  const [formName, setFormName] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const queryClient = useQueryClient();

  const { data: branches, isLoading } = useQuery({
    queryKey: ['branches'],
    queryFn: () => branchesApi.list(),
  });

  const { data: cameras } = useQuery({
    queryKey: ['cameras'],
    queryFn: () => camerasApi.list(),
  });

  const { data: mediaPlayers } = useQuery({
    queryKey: ['media-players'],
    queryFn: () => mediaPlayersApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; address?: string | null }) =>
      branchesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      closeModal();
      toast.success('Branch created');
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        toast.error('Branch name already exists');
      } else {
        toast.error('Failed to create branch');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; address?: string | null } }) =>
      branchesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      closeModal();
      toast.success('Branch updated');
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        toast.error('Branch name already exists');
      } else {
        toast.error('Failed to update branch');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => branchesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      queryClient.invalidateQueries({ queryKey: ['media-players'] });
      setShowDeleteConfirm(null);
      toast.success('Branch deleted. Devices moved to unassigned.');
    },
    onError: () => {
      toast.error('Failed to delete branch');
    },
  });

  const closeModal = () => {
    setShowAddModal(false);
    setEditingBranch(null);
    setFormName('');
    setFormAddress('');
  };

  const openEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setFormName(branch.name);
    setFormAddress(branch.address || '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBranch) {
      updateMutation.mutate({
        id: editingBranch.id,
        data: { name: formName, address: formAddress || null },
      });
    } else {
      createMutation.mutate({
        name: formName,
        address: formAddress || null,
      });
    }
  };

  const getCameraCount = (branchId: string) =>
    cameras?.filter((c) => c.branch_id === branchId).length || 0;

  const getPlayerCount = (branchId: string) =>
    mediaPlayers?.filter((mp) => mp.branch_id === branchId).length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Branches</h1>
          <p className="text-gray-500 dark:text-slate-400">Manage your store locations</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Branch
        </Button>
      </div>

      {/* Branches Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500 dark:text-slate-400">Loading branches...</div>
      ) : branches && branches.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((branch) => (
            <Card key={branch.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-primary-50 rounded-lg">
                    <Building2 className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{branch.name}</h3>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openEdit(branch)}
                    className="p-1 text-gray-400 dark:text-slate-500 hover:text-primary-500 dark:hover:text-primary-400 rounded"
                    title="Edit branch"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(branch)}
                    className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 rounded"
                    title="Delete branch"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {branch.address && (
                <div className="flex items-start text-sm text-gray-500 dark:text-slate-400 mb-3">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
                  <span>{branch.address}</span>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t dark:border-slate-700 text-sm text-gray-600 dark:text-slate-300">
                <div className="flex items-center space-x-1">
                  <Camera className="w-4 h-4" />
                  <span>{getCameraCount(branch.id)} cameras</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Tv className="w-4 h-4" />
                  <span>{getPlayerCount(branch.id)} screens</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto text-gray-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No branches yet</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4">
            Create branches to organize cameras and screens by store location
          </p>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Branch
          </Button>
        </Card>
      )}

      {/* Add / Edit Modal */}
      {(showAddModal || editingBranch) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Card className="w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded">
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Branch Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Mall of Africa Store"
                required
              />
              <Input
                label="Address (optional)"
                value={formAddress}
                onChange={(e) => setFormAddress(e.target.value)}
                placeholder="e.g., Magwa Cres, Waterfall City, Midrand"
              />
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={createMutation.isPending || updateMutation.isPending}
                >
                  {editingBranch ? 'Save Changes' : 'Create Branch'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Card className="w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">Delete Branch</h2>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>?
                All cameras and screens in this branch will become unassigned.
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(showDeleteConfirm.id)}
                isLoading={deleteMutation.isPending}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Branch
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
